import React from 'react';

import { Button, Group, LoadingOverlay, Modal, Stack, Title, Text } from '@mantine/core';
import { AxiosError, AxiosResponse } from 'axios';
import { UseMutationResult } from 'react-query';
import { ErrorMessage, PayrollPeriod, PayrollPeriodUpdate } from 'types';
import notificationHandler from 'utils/notifications';

interface IModalGeneratePayrollPeriodProps {
  opened: boolean;
  handleClose: () => void;
  payrollPeriod?: PayrollPeriod;
  updateMutation: UseMutationResult<
    AxiosResponse<PayrollPeriod>,
    AxiosError<ErrorMessage>,
    PayrollPeriodUpdate
  >;
}

const ModalGeneratePayrollPeriod = ({
  opened,
  handleClose,
  payrollPeriod,
  updateMutation,
}: IModalGeneratePayrollPeriodProps): JSX.Element => {
  const { successNotification, errorNotification } = notificationHandler();
  const handleSubmit = (): void => {
    if (payrollPeriod) {
      updateMutation.mutate(
        {
          ...payrollPeriod,
          action: 'generate',
        },
        {
          onSuccess: () => {
            successNotification('Generate payroll period success');
            handleClose();
          },
          onError: error => {
            errorNotification('Generate payroll period failed', error.response?.data.message);
          },
        },
      );
    }
  };

  return (
    <Modal
      centered
      opened={opened}
      onClose={handleClose}
      title={<Title order={4}>Generate Payroll Report</Title>}
      size='auto'
    >
      <LoadingOverlay visible={!payrollPeriod} />
      <Stack>
        <Text>Are you sure you want proceed with this action?</Text>
        <Text color='dimmed'>You will rewrite existing data.</Text>
        <Group position='right'>
          <Button
            variant='subtle'
            onClick={handleClose}
            disabled={updateMutation.isLoading || !payrollPeriod}
            data-cy='payrollPeriodCancelButton'
          >
            Cancel
          </Button>
          <Button
            loading={updateMutation.isLoading}
            onClick={handleSubmit}
            disabled={!payrollPeriod}
            data-cy='payrollPeriodGenerateSubmitButton'
          >
            Save
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default ModalGeneratePayrollPeriod;
